import React, { useState, useEffect, useRef } from "react";
import {
  ArrowRight,
  Boxes,
  BriefcaseBusiness,
  Check,
  Factory,
  Handshake,
  Menu,
  Package,
  Users,
  WalletCards,
  X,
  ShoppingCart,
  ShieldCheck,
  Truck,
  Target,
  Cog,
  Landmark,
  FileCheck2,
  Building2,
  UserCheck,
  ClipboardCheck,
  CircleDollarSign,
  BadgeCheck,
} from "lucide-react";
import Logo from "./synlogo.png";
import "./NewLandingPage.css";

const defaultItemCategories = [
  "BUY",
  "SELL",
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const EMAIL_EXISTS_MESSAGE =
  "This email already exists. Please use a different email.";

// Calls backend: GET /api/check-email?email=xxx  ->  { exists: true | false }
// Returns true if the email already exists, false otherwise.
// If the server cannot be reached we return false (the backend must still
// reject duplicates on save).
async function checkEmailExists(email, signal) {
  try {
    const res = await fetch(
      `/api/check-email?email=${encodeURIComponent(email.trim())}`,
      { signal },
    );
    if (!res.ok) return false;
    const data = await res.json();
    return Boolean(data.exists);
  } catch (err) {
    if (err.name === "AbortError") throw err;
    console.error("Email check failed:", err);
    return false;
  }
}

const consulting = {
  Marketing: {
    Icon: Target,
    tagline: "Right Market. Right Strategy. Right Growth.",
    intro:
      "We help businesses understand where the next opportunity lies and how to convert market potential into sustainable revenue.",
    focus: [
      "Market Research & Opportunity Analysis",
      "Customer & Market Segmentation",
      "Brand Positioning",
      "Go-to-Market Strategy",
      "Sales & Distribution Strategy",
      "New Product / Market Development",
      "Customer Acquisition",
      "Marketing Performance & ROI",
    ],
    outcomes: [
      "Stronger Market Presence",
      "Higher Customer Acquisition",
      "Increased Sales",
      "Better Market Penetration",
      "Improved Marketing ROI",
      "Sustainable Business Growth",
    ],
  },
  Materials: {
    Icon: Package,
    tagline: "Right Material. Right Source. Right Cost.",
    intro:
      "We help businesses improve sourcing, procurement and inventory while building stronger and more reliable supply chains.",
    focus: [
      "Strategic Sourcing & Procurement",
      "Vendor Identification & Development",
      "Alternate Source Development",
      "Purchase Cost Optimisation",
      "Raw Material Market Intelligence",
      "Inventory Optimisation",
      "Supplier Performance Management",
      "Import / Export Sourcing",
      "Working Capital Optimisation",
    ],
    outcomes: [
      "Lower Purchase Cost",
      "Reliable & Alternate Sources",
      "Reduced Inventory Investment",
      "Improved Working Capital",
      "Better Supply Continuity",
      "Stronger Supplier Network",
    ],
  },
  Manpower: {
    Icon: Users,
    tagline: "Right People. Right Skills. Right Productivity.",
    intro:
      "We help businesses build accountability, productivity and capability while reducing excessive dependency on individuals.",
    focus: [
      "Organisation Structure",
      "Manpower Planning",
      "Roles & Responsibilities",
      "Recruitment & Talent Identification",
      "Skill Gap Analysis",
      "Training & Capability Development",
      "Performance Management",
      "Productivity Improvement",
      "Employee Retention",
      "Succession Planning",
    ],
    outcomes: [
      "Higher Employee Productivity",
      "Better Accountability",
      "Improved Skill Utilisation",
      "Reduced Individual Dependency",
      "Lower Manpower Cost",
      "Stronger Leadership Pipeline",
    ],
  },
  Machines: {
    Icon: Cog,
    tagline: "Right Technology. Right Capacity. Right Productivity.",
    intro:
      "We help businesses ensure their machines and technology deliver maximum productivity and return on investment.",
    focus: [
      "Capacity Utilisation Analysis",
      "Machine Productivity",
      "OEE Improvement",
      "Bottleneck Identification",
      "Machine Selection & Evaluation",
      "Automation Opportunities",
      "Preventive & Predictive Maintenance",
      "Energy Efficiency",
      "Process Improvement",
      "CAPEX Evaluation & ROI",
    ],
    outcomes: [
      "Higher Machine Utilisation",
      "Increased Production Capacity",
      "Reduced Downtime",
      "Lower Manufacturing Cost",
      "Improved Product Quality",
      "Better Return on Investment",
    ],
  },
  Money: {
    Icon: WalletCards,
    tagline: "Right Capital. Right Control. Right Returns.",
    intro:
      "We help management understand where money is invested, where it is blocked and where profitability can be improved.",
    focus: [
      "Working Capital Management",
      "Cash Flow Management",
      "Cost & Profitability Analysis",
      "Product Profitability",
      "Customer Profitability",
      "Receivables & Payables",
      "Inventory Investment",
      "Budgeting & Financial Planning",
      "Cost Reduction",
      "CAPEX & Investment Evaluation",
      "Management MIS",
      "Decision Support",
    ],
    outcomes: [
      "Improved Cash Flow",
      "Reduced Working Capital",
      "Higher Profitability",
      "Better Cost Control",
      "Faster Receivables",
      "Improved ROI",
      "Better Management Decisions",
    ],
  },
};

const erpModules = [
  {
    title: "Material Management",
    Icon: Package,
    tagline: "Buy Better. Stock Smarter. Control Your Cost.",
    covers: [
      "Purchase Requisition",
      "Enquiry & Quotation Comparison",
      "Purchase Order",
      "Supplier Management",
      "Material Receipt",
      "GRN",
      "Quality Hold / Release",
      "Stores & Inventory",
      "Batch / Lot Tracking",
      "Stock Transfer",
      "Material Issue",
      "Consumption",
      "Reorder / Minimum Stock",
      "Inventory Valuation",
      "Supplier Performance",
    ],
    advantage:
      "Know what you are buying, from whom, at what cost, what is lying in stock and where your money is blocked.",
  },
  {
    title: "Sales & Distribution",
    Icon: ShoppingCart,
    tagline: "From Enquiry to Collection — One Connected Sales Cycle.",
    covers: [
      "Customer Master",
      "Enquiry Management",
      "Quotation",
      "Price Management",
      "Sales Order",
      "Order Status",
      "Availability / Commitment",
      "Dispatch Planning",
      "Delivery",
      "Invoicing",
      "Customer Outstanding",
      "Sales Analysis",
      "Customer Profitability",
      "Sales MIS",
    ],
    advantage:
      "Know not only how much you sold — know what you sold, to whom, at what margin and what is still to be collected.",
  },
  {
    title: "Production Management",
    Icon: Factory,
    tagline: "Plan Better. Produce Better. Waste Less.",
    covers: [
      "Production Planning",
      "Production Orders",
      "BOM / Product Structure",
      "Material Requirement",
      "Work-in-Progress",
      "Material Consumption",
      "Production Output",
      "Process Tracking",
      "Rejection",
      "Rework",
      "Machine / Capacity Planning",
      "Production Costing",
      "Production MIS",
    ],
    advantage:
      "See what should be produced, what is being produced, what material is consumed and where production efficiency is being lost.",
  },
  {
    title: "Quality Management",
    Icon: ShieldCheck,
    tagline: "Quality Built Into the Process — Not Checked Only at the End.",
    covers: [
      "Incoming Material Inspection",
      "In-Process Inspection",
      "Final Inspection",
      "Quality Parameters",
      "Specification Management",
      "Acceptance / Rejection",
      "Non-Conformance",
      "Rework",
      "Rejection Analysis",
      "Supplier Quality",
      "Customer Complaints",
      "Quality MIS",
      "Traceability",
    ],
    advantage:
      "Move from “detecting defects” to understanding why defects happen and where they originate.",
  },
  {
    title: "HR & Manpower Management",
    Icon: Users,
    tagline: "Right People. Right Responsibility. Right Productivity.",
    covers: [
      "Employee Master",
      "Organisation Structure",
      "Department / Designation",
      "Attendance",
      "Leave",
      "Shift Management",
      "Employee Documents",
      "Payroll Inputs",
      "Performance Parameters",
      "Training Records",
      "Skill Matrix",
      "Manpower MIS",
    ],
    advantage:
      "Know who is responsible, what capability exists, where the skill gaps are and where manpower productivity can improve.",
  },
  {
    title: "Accounts & Finance",
    Icon: Landmark,
    tagline: "Don't Just Record Money. Understand Your Money.",
    covers: [
      "Chart of Accounts",
      "General Ledger",
      "Accounts Payable",
      "Accounts Receivable",
      "Customer Outstanding",
      "Supplier Outstanding",
      "Receipts & Payments",
      "Bank / Cash",
      "Costing",
      "Profitability",
      "Tax-related accounting processes",
      "Financial MIS",
      "Management Reports",
    ],
    advantage:
      "Know where your money is earned, where it is blocked, where costs are increasing and where profitability can improve.",
  },
];

const connectSteps = [
  "Register",
  "Post",
  "Verify",
  "Match",
  "Qualify",
  "Confidential Enquiry",
  "Negotiate",
  "Confirm",
  "Connect",
  "PO",
  "Success Fee",
];

const existingClients = [
  {
    id: 1,
    name: "Shripad Polymer",
    logo: "https://shripad-syn8erp.azurewebsites.net/Images/Shripad%20LOGO.png",
  },
  {
    id: 2,
    name: "Vistta",
    logo: "https://synergy5m-visttaerp8-uat.azurewebsites.net/Images/VistaLOGOEdited.JPG",
  },
  {
    id: 3,
    name: "Chemikar",
    logo: "https://synergy5m-chemikarerp8.azurewebsites.net/Images/Logo(1).jpg",
  },
  {
    id: 4,
    name: "Swami Samarth",
    logo: "https://synergy5m-swamisamartherp8.azurewebsites.net/Images/SwamiSamarthLogo.png",
  },
];

const buyerRoles = [
  "Owner / Promoter",
  "Director",
  "Partner",
  "Purchase Head",
  "Procurement Manager",
  "Senior Purchase Executive",
  "Supply Chain Head",
  "Other authorised senior employee",
];

const sellerRoles = [
  "Owner / Promoter",
  "Director",
  "Partner",
  "Sales Head",
  "Business Development Head",
  "Marketing Head",
  "Senior Sales Executive",
  "Export Head",
  "Other authorised senior employee",
];

function Field({ label, children, required = false, hint, className = "" }) {
  return (
    <label className={`form-field ${className}`.trim()}>
      <span>
        {label}
        {required && <em> *</em>}
      </span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}

/**
 * Email input with REAL-TIME duplicate check.
 * - While the user types (debounced 500ms) it asks the backend if the email exists.
 * - If it exists: red error is shown and the input gets a custom validity,
 *   so the browser blocks form submit (data is NOT saved).
 */
function EmailField({
  label,
  name,
  placeholder,
  required = false,
  className = "",
}) {
  const [value, setValue] = useState("");
  const [status, setStatus] = useState("idle"); // idle | checking | exists | available
  const inputRef = useRef(null);

  useEffect(() => {
    const email = value.trim();
    const input = inputRef.current;

    // Nothing to check yet
    if (!email || !EMAIL_REGEX.test(email)) {
      setStatus("idle");
      if (input) input.setCustomValidity("");
      return undefined;
    }

    setStatus("checking");
    if (input) input.setCustomValidity("");

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const exists = await checkEmailExists(email, controller.signal);
        if (exists) {
          setStatus("exists");
          if (inputRef.current)
            inputRef.current.setCustomValidity(EMAIL_EXISTS_MESSAGE);
        } else {
          setStatus("available");
          if (inputRef.current) inputRef.current.setCustomValidity("");
        }
      } catch (err) {
        // aborted because user kept typing - ignore
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [value]);

  const hasError = status === "exists";

  return (
    <Field
      label={label}
      required={required}
      className={className}
      hint={
        hasError ? (
          <span style={{ color: "#d62828", fontWeight: 600 }}>
            {EMAIL_EXISTS_MESSAGE}
          </span>
        ) : status === "checking" ? (
          <span style={{ color: "#607089" }}>Checking email...</span>
        ) : null
      }
    >
      <input
        ref={inputRef}
        required={required}
        name={name}
        type="email"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        aria-invalid={hasError}
        style={hasError ? { borderColor: "#d62828", outlineColor: "#d62828" } : undefined}
      />
    </Field>
  );
}

function SearchableDropdown({
  label,
  name,
  options = [],
  required = false,
  value,
  onChange,
  disabled = false,
  placeholder = "--- SELECT ---",
  hint,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options.filter((opt) =>
    String(opt).toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleSelect = (option) => {
    if (onChange) {
      onChange({ target: { name, value: option } });
    }
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <div
      className="form-field"
      ref={dropdownRef}
      style={{ position: "relative" }}
    >
      <span>
        {label}
        {required && <em> *</em>}
      </span>

      <input
        type="hidden"
        name={name}
        value={value || ""}
        required={required}
      />

      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          border: "1px solid #d5dee9",
          borderRadius: "6px",
          backgroundColor: disabled ? "#f1f3f5" : "#fff",
          cursor: disabled ? "not-allowed" : "pointer",
          fontWeight: value ? "600" : "400",
          color: value ? "#102d50" : "#607089",
          userSelect: "none",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value || placeholder}
        </span>
        <span style={{ fontSize: "10px", color: "#607089", marginLeft: "8px" }}>▼</span>
      </div>

      {isOpen && !disabled && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 999,
            backgroundColor: "#fff",
            border: "1px solid #0b438d",
            borderRadius: "6px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            marginTop: "4px",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
            <input
              type="text"
              autoFocus
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 10px",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                fontSize: "12px",
                outline: "none",
              }}
            />
          </div>

          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <div
                  key={opt}
                  onClick={() => handleSelect(opt)}
                  style={{
                    padding: "9px 14px",
                    cursor: "pointer",
                    fontSize: "12px",
                    backgroundColor: value === opt ? "#f0f5fb" : "transparent",
                    color: value === opt ? "#0b438d" : "#102d50",
                    fontWeight: value === opt ? "700" : "400",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f5f8fc")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor =
                      value === opt ? "#f0f5fb" : "transparent")
                  }
                >
                  {opt}
                </div>
              ))
            ) : (
              <div
                style={{
                  padding: "10px",
                  fontSize: "11px",
                  color: "#888",
                  textAlign: "center",
                }}
              >
                No matches found
              </div>
            )}
          </div>
        </div>
      )}

      {hint && <small>{hint}</small>}
    </div>
  );
}

function CompanyVerificationFields({
  industries = [],
  selectedIndustry,
  onIndustryChange,
  countryCode,
  setCountryCode,
  mobileNumber,
  setMobileNumber,
}) {
  return (
    <>
      <div className="form-section-title">
        <Building2 size={18} /> Company & Verification
      </div>
      <div className="form-grid three">
        <Field label="Company Name" required>
          <input
            required
            name="companyName"
            placeholder="Registered company name"
          />
        </Field>
        <Field label="GSTIN">
          <input
            name="gstin"
            pattern="[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]"
            title="Enter a valid 15-character GSTIN"
            placeholder="15-character GSTIN"
          />
        </Field>
        <Field label="CIN / LLPIN">
          <input
            name="cin"
            pattern="[A-Za-z0-9-]{6,30}"
            title="Enter your CIN or LLPIN"
            placeholder="CIN or LLPIN"
          />
        </Field>
        <Field label="Registered Address" required>
          <input required name="address" placeholder="Registered address" />
        </Field>
        <Field label="Company Website">
          <input name="website" type="url" placeholder="https://" />
        </Field>

        {/* Real-time duplicate email check */}
        <EmailField
          label="Company Email"
          name="companyEmail"
          required
          placeholder="official@company.com"
        />

        <div className="form-group">
          <label style={styles.label}>Official Mobile Number <span style={{color: 'red'}}>*</span></label>
          <div style={{ display: "flex", gap: "8px", width: "100%" }}>
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              style={{ flex: "0 0 135px", padding: "10px 8px", borderRadius: "8px", border: "1px solid #D8DCE0", fontSize: "13.5px", background: "#fff" }}
            >
              <option value="+91">🇮🇳 +91 (India)</option>
              <option value="+1">🇺🇸 +1 (USA/Canada)</option>
              <option value="+44">🇬🇧 +44 (UK)</option>
              <option value="+971">🇦🇪 +971 (UAE)</option>
              <option value="+61">🇦🇺 +61 (Australia)</option>
              <option value="+65">🇸🇬 +65 (Singapore)</option>
              <option value="+49">🇩🇪 +49 (Germany)</option>
            </select>
            <input
              required
              name="mobile"
              type="tel"
              pattern="[0-9]{7,12}"
              placeholder="Enter mobile number"
              value={mobileNumber}
              onChange={(e) => setMobileNumber(e.target.value)}
              style={{ flex: "1", padding: "10px 12px", borderRadius: "8px", border: "1px solid #D8DCE0", fontSize: "14px", background: "#fff" }}
            />
          </div>
        </div>

        <SearchableDropdown
          label="Industry"
          name="industry"
          required
          value={selectedIndustry}
          options={industries}
          placeholder="Select Industry"
          onChange={(e) => onIndustryChange && onIndustryChange(e.target.value)}
        />

        <Field label="Company Type" required>
          <select required name="companyType" defaultValue="">
            <option value="" disabled>
              Select company type
            </option>
            <option>Private Limited</option>
            <option>Public Limited</option>
            <option>LLP</option>
            <option>Partnership</option>
            <option>Proprietorship</option>
            <option>Other registered business</option>
          </select>
        </Field>
        <Field label="Years in Business">
          <input name="years" type="number" min="0" placeholder="Years" />
        </Field>
      </div>
      <div className="verification-note">
        <FileCheck2 size={18} />
        <div>
          <b>Business verification</b>
          <span>
            Registration is intended for eligible registered businesses. GSTIN
            and CIN / LLPIN are mandatory fields.
          </span>
        </div>
      </div>
    </>
  );
}

function RegistrationBlock({
  type,
  industries = [],
  selectedIndustry,
  onIndustryChange,
  countryCode,
  setCountryCode,
  mobileNumber,
  setMobileNumber,
}) {
  const roles = type === "buyer" ? buyerRoles : sellerRoles;
  return (
    <>
      <CompanyVerificationFields
        industries={industries}
        selectedIndustry={selectedIndustry}
        onIndustryChange={onIndustryChange}
        countryCode={countryCode}
        setCountryCode={setCountryCode}
        mobileNumber={mobileNumber}
        setMobileNumber={setMobileNumber}
      />
      <div className="form-section-title">
        <UserCheck size={18} /> Authorised Representative
      </div>
      <div className="form-grid three">
        <Field label="Full Name" required>
          <input
            required
            name="representativeName"
            placeholder="Authorised person's name"
          />
        </Field>
        <Field label="Designation / Business Role">
          <select name="role" defaultValue="">
            <option value="" disabled>
              Select your role
            </option>
            {roles.map((role) => (
              <option key={role}>{role}</option>
            ))}
          </select>
        </Field>

        {/* Real-time duplicate email check */}
        <EmailField
          label="Representative Email"
          name="representativeEmail"
          required
          placeholder="Work email"
        />

        <Field label="Representative Mobile" required>
          <input
            required
            name="representativeMobile"
            type="tel"
            placeholder="Work mobile"
          />
        </Field>
      </div>
      <label className="authority-check">
        <input type="checkbox" name="authority" />
        <span>
          I confirm that I am authorised by the organisation to submit business
          requirements / offers through Synergy5M Business Connect.
        </span>
      </label>
    </>
  );
}

function CommissionBlock() {
  return (
    <div className="commission-box">
      <div className="form-section-title">
        <CircleDollarSign size={18} /> Business Commission / Success Fee
      </div>
      <p>
        What commercial commission can your company offer Synergy5M if this
        business is successfully concluded through our introduction?
      </p>
      <div className="form-grid three">
        <Field label="Commission Type">
          <select name="commissionType" defaultValue="">
            <option value="" disabled>
              Select
            </option>
            <option>Percentage of Purchase Order Value</option>
            <option>Fixed Amount</option>
            <option>Per Unit</option>
            <option>Other</option>
          </select>
        </Field>
        <Field label="Proposed Commission">
          <input name="proposedCommission" placeholder="% / ₹ amount" />
        </Field>
        <Field label="Commission Applicable On">
          <select name="commissionApplicableOn" defaultValue="">
            <option value="" disabled>
              Select
            </option>
            <option>First Purchase Order</option>
            <option>Every Purchase Order for agreed period</option>
            <option>Entire business relationship for agreed period</option>
            <option>Other agreed arrangement</option>
          </select>
        </Field>
      </div>
      <small>
        The final commercial terms will be mutually agreed and documented before
        disclosure of the parties' identities / commercial contact details.
      </small>
    </div>
  );
}

function BuyerForm({
  categories = defaultItemCategories,
  units = [],
  currencies = [],
  industries = [],
  countryCode,
  setCountryCode,
  mobileNumber,
  setMobileNumber,
}) {
  const [selectedCategory, setSelectedCategory] = useState("");
  const [customCategory, setCustomCategory] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [customProduct, setCustomProduct] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(false);

  const [selectedUnit, setSelectedUnit] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState("");

  useEffect(() => {
    if (!selectedCategory || selectedCategory === "Other / Add New") {
      setProducts([]);
      setSelectedProduct("");
      return;
    }
    setLoadingProducts(true);
    fetch(
      `/api/products?category=${encodeURIComponent(selectedCategory.trim())}`,
    )
      .then((res) => res.json())
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("Failed to fetch products:", err);
        setProducts([]);
      })
      .finally(() => setLoadingProducts(false));
  }, [selectedCategory]);

  return (
    <>
      <RegistrationBlock
        type="buyer"
        industries={industries}
        selectedIndustry={selectedIndustry}
        onIndustryChange={setSelectedIndustry}
        countryCode={countryCode}
        setCountryCode={setCountryCode}
        mobileNumber={mobileNumber}
        setMobileNumber={setMobileNumber}
      />

      <div className="form-section-title">
        <ClipboardCheck size={18} /> Post a Buying Requirement
      </div>

      <div className="form-grid three">
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <SearchableDropdown
            label="ITEM CATEGORY"
            name="productCategory"
            required
            value={selectedCategory}
            options={categories}
            placeholder="--- SELECT CATEGORY ---"
            onChange={(e) => {
              const val = e.target.value;
              setSelectedCategory(val);
              setSelectedProduct("");
              setCustomCategory("");
              setCustomProduct("");
            }}
          />
          {selectedCategory === "Other / Add New" && (
            <Field label="Enter New Category" required>
              <input
                name="productCategory"
                required
                placeholder="Type new category name"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
              />
            </Field>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <SearchableDropdown
            label="Product Name"
            name="productName"
            required
            value={selectedProduct}
            options={
              selectedCategory === "Other / Add New"
                ? ["Other / Add New"]
                : [...products, "Other / Add New"]
            }
            disabled={!selectedCategory || loadingProducts}
            placeholder={
              !selectedCategory
                ? "Select Category First"
                : loadingProducts
                  ? "Loading products..."
                  : products.length === 0
                    ? "No products found (Add New)"
                    : "Select Product"
            }
            onChange={(e) => {
              setSelectedProduct(e.target.value);
              setCustomProduct("");
            }}
          />
          {(selectedProduct === "Other / Add New" ||
            selectedCategory === "Other / Add New") && (
            <Field label="Enter New Product Name" required>
              <input
                name="productName"
                required
                placeholder="Type new product name"
                value={customProduct}
                onChange={(e) => setCustomProduct(e.target.value)}
              />
            </Field>
          )}
        </div>

        <Field label="Application / End Use" required>
          <input
            name="application"
            required
            placeholder="Application / end use"
          />
        </Field>
        <Field label="Grade / Model">
          <input name="gradeModel" placeholder="Grade or model" />
        </Field>
        <Field label="Brand Preference">
          <input name="brandPreference" placeholder="Preferred brand, if any" />
        </Field>
        <Field label="HSN Code">
          <input name="hsnCode" placeholder="HSN code" />
        </Field>
        <Field label="Required Certification / Standard">
          <input
            name="requiredCertification"
            placeholder="Certification / standard"
          />
        </Field>
        <Field label="Required Quantity" required>
          <input name="requiredQuantity" required placeholder="Quantity" />
        </Field>

        <SearchableDropdown
          label="Unit"
          name="unit"
          required
          value={selectedUnit}
          options={units}
          placeholder="Select Unit"
          onChange={(e) => setSelectedUnit(e.target.value)}
        />

        <Field label="Requirement Frequency">
          <select name="requirementFrequency" defaultValue="">
            <option value="" disabled>
              Select
            </option>
            <option>One-Time</option>
            <option>Recurring</option>
            <option>Monthly</option>
            <option>Quarterly</option>
            <option>Annual</option>
          </select>
        </Field>
        <Field label="Expected Price Range">
          <input name="expectedPriceRange" placeholder="Price range" />
        </Field>
        <Field label="Target Price">
          <input name="targetPrice" placeholder="Target price" />
        </Field>

        <SearchableDropdown
          label="Currency"
          name="currency"
          value={selectedCurrency}
          options={currencies}
          placeholder="Select Currency"
          onChange={(e) => setSelectedCurrency(e.target.value)}
        />

        <Field label="Payment Terms Expected">
          <input name="paymentTermsExpected" placeholder="Payment terms" />
        </Field>
        <Field label="Credit Period">
          <input name="creditPeriod" placeholder="Credit period" />
        </Field>
        <Field label="Delivery Location" required>
          <input
            name="deliveryLocation"
            required
            placeholder="City / State / Country"
          />
        </Field>
        <Field label="Required Delivery Date" required>
          <input name="requiredDeliveryDate" required type="date" />
        </Field>
        <Field label="Preferred Incoterm">
          <input name="preferredIncoterm" placeholder="If applicable" />
        </Field>
        <Field label="Domestic / Import Requirement">
          <select name="domesticOrImport" defaultValue="">
            <option value="" disabled>
              Select
            </option>
            <option>Domestic</option>
            <option>Import</option>
            <option>Either</option>
          </select>
        </Field>
        <Field label="Supplier Preference">
          <select name="supplierPreference" defaultValue="">
            <option value="" disabled>
              Select
            </option>
            <option>Manufacturer</option>
            <option>Authorised Distributor</option>
            <option>Exporter</option>
            <option>Trader</option>
            <option>OEM</option>
            <option>Any suitable supplier</option>
          </select>
        </Field>
        <Field label="Preferred Origin / Country">
          <input name="preferredOrigin" placeholder="Preferred origin" />
        </Field>
        <Field label="Certifications Required">
          <input
            name="certificationsRequired"
            placeholder="Required supplier certifications"
          />
        </Field>
        <Field label="Minimum Supplier Experience">
          <input
            name="minSupplierExperience"
            placeholder="Years/experience"
          />
        </Field>

        <div className="textarea-row">
          <Field label="Technical Specification" required>
            <textarea
              name="technicalSpecification"
              required
              rows="3"
              placeholder="Technical specification"
            />
          </Field>
          <Field label="Additional Information">
            <textarea
              name="additionalInfo"
              rows="3"
              placeholder="Additional remarks"
            />
          </Field>
        </div>
      </div>
      <CommissionBlock />
    </>
  );
}

function SellerForm({
  categories = defaultItemCategories,
  currencies = [],
  industries = [],
  countryCode,
  setCountryCode,
  mobileNumber,
  setMobileNumber,
}) {
  const [selectedCategory, setSelectedCategory] = useState("");
  const [customCategory, setCustomCategory] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [customProduct, setCustomProduct] = useState("");
  const [loadingProducts, setLoadingProducts] = useState(false);

  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState("");

  useEffect(() => {
    if (!selectedCategory || selectedCategory === "Other / Add New") {
      setProducts([]);
      setSelectedProduct("");
      return;
    }
    setLoadingProducts(true);
    fetch(
      `/api/products?category=${encodeURIComponent(selectedCategory.trim())}`,
    )
      .then((res) => res.json())
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("Failed to fetch products:", err);
        setProducts([]);
      })
      .finally(() => setLoadingProducts(false));
  }, [selectedCategory]);

  return (
    <>
      <RegistrationBlock
        type="seller"
        industries={industries}
        selectedIndustry={selectedIndustry}
        onIndustryChange={setSelectedIndustry}
        countryCode={countryCode}
        setCountryCode={setCountryCode}
        mobileNumber={mobileNumber}
        setMobileNumber={setMobileNumber}
      />
      <div className="form-section-title">
        <Factory size={18} /> List Your Product / Selling Opportunity
      </div>
      <div className="form-grid three">
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <SearchableDropdown
            label="ITEM CATEGORY"
            name="productCategory"
            required
            value={selectedCategory}
            options={categories}
            placeholder="--- SELECT CATEGORY ---"
            onChange={(e) => {
              const val = e.target.value;
              setSelectedCategory(val);
              setSelectedProduct("");
              setCustomCategory("");
              setCustomProduct("");
            }}
          />
          {selectedCategory === "Other / Add New" && (
            <Field label="Enter New Category" required>
              <input
                name="productCategory"
                required
                placeholder="Type new category name"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
              />
            </Field>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <SearchableDropdown
            label="Product Name"
            name="productName"
            required
            value={selectedProduct}
            options={
              selectedCategory === "Other / Add New"
                ? ["Other / Add New"]
                : [...products, "Other / Add New"]
            }
            disabled={!selectedCategory || loadingProducts}
            placeholder={
              !selectedCategory
                ? "Select Category First"
                : loadingProducts
                  ? "Loading products..."
                  : products.length === 0
                    ? "No products found (Add New)"
                    : "Select Product"
            }
            onChange={(e) => {
              setSelectedProduct(e.target.value);
              setCustomProduct("");
            }}
          />
          {(selectedProduct === "Other / Add New" ||
            selectedCategory === "Other / Add New") && (
            <Field label="Enter New Product Name" required>
              <input
                name="productName"
                required
                placeholder="Type new product name"
                value={customProduct}
                onChange={(e) => setCustomProduct(e.target.value)}
              />
            </Field>
          )}
        </div>

        <Field label="Manufacturer / Supplier" required>
          <input
            name="manufacturerSupplier"
            required
            placeholder="Manufacturer / supplier"
          />
        </Field>
        <Field label="Grade / Model" required>
          <input name="gradeModel" required placeholder="Grade / model" />
        </Field>
        <Field label="Application">
          <input name="application" placeholder="Application" />
        </Field>
        <Field label="HSN Code">
          <input name="hsnCode" placeholder="HSN code" />
        </Field>

        <div className="textarea-row">
          <Field label="Product Description">
            <textarea
              name="productDescription"
              rows="3"
              placeholder="Product description"
            />
          </Field>
          <Field label="Technical Specification">
            <textarea
              name="technicalSpecification"
              rows="3"
              placeholder="Technical specification"
            />
          </Field>
        </div>

        <Field label="Capacity">
          <input name="capacity" placeholder="Capacity" />
        </Field>
        <Field label="Production Capacity">
          <input name="productionCapacity" placeholder="Production capacity" />
        </Field>
        <Field label="Available Capacity">
          <input name="availableCapacity" placeholder="Available capacity" />
        </Field>
        <Field label="MOQ">
          <input name="moq" placeholder="Minimum order quantity" />
        </Field>
        <Field label="Monthly Capacity">
          <input name="monthlyCapacity" placeholder="Monthly capacity" />
        </Field>
        <Field label="Lead Time">
          <input name="leadTime" placeholder="Lead time" />
        </Field>
        <Field label="Indicative Price / Price Range">
          <input name="indicativePrice" placeholder="Price / range" />
        </Field>

        <SearchableDropdown
          label="Currency"
          name="currency"
          value={selectedCurrency}
          options={currencies}
          placeholder="Select Currency"
          onChange={(e) => setSelectedCurrency(e.target.value)}
        />

        <Field label="Payment Terms">
          <input name="paymentTerms" placeholder="Payment terms" />
        </Field>
        <Field label="Credit Terms">
          <input name="creditTerms" placeholder="Credit terms" />
        </Field>
        <Field label="Domestic / Export">
          <select name="domesticOrExport" defaultValue="">
            <option value="" disabled>
              Select
            </option>
            <option>Domestic</option>
            <option>Export</option>
            <option>Both</option>
          </select>
        </Field>
        <Field label="Geography">
          <input name="geography" placeholder="Target geography" />
        </Field>
        <Field label="Supply Locations">
          <input name="supplyLocations" placeholder="Supply locations" />
        </Field>
        <Field label="Countries Served">
          <input name="countriesServed" placeholder="Countries served" />
        </Field>
        <Field label="Preferred Buyer Location">
          <input
            name="preferredBuyerLocation"
            placeholder="Preferred buyer location"
          />
        </Field>
        <Field label="Years in Business">
          <input name="sellerYearsInBusiness" placeholder="Years" />
        </Field>
        <Field label="Certifications">
          <input name="certifications" placeholder="Certifications" />
        </Field>
        <Field label="Major Industries Served">
          <input name="majorIndustriesServed" placeholder="Industries served" />
        </Field>
        <Field label="OEM Capability">
          <select name="oemCapability" defaultValue="">
            <option value="" disabled>
              Select
            </option>
            <option>Yes</option>
            <option>No</option>
          </select>
        </Field>
        <Field label="Private Label Capability">
          <select name="privateLabelCapability" defaultValue="">
            <option value="" disabled>
              Select
            </option>
            <option>Yes</option>
            <option>No</option>
          </select>
        </Field>
      </div>
      <CommissionBlock />
    </>
  );
}

function Modal({ close, children, title, subtitle, wide = false, onBookDemo }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  return (
    <div className="modal-backdrop" onClick={close}>
      <div
        className={`modal ${wide ? "modal-wide" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={close} type="button" aria-label="Close modal">
          <X size={18} />
        </button>

        <div className="modal-top-bar">
          <span className="section-kicker orange" style={{ margin: 0 }}>
            SYNERGY5M LLP
          </span>

          {onBookDemo && (
            <button
              className="expert-btn modal-header-action"
              type="button"
              onClick={onBookDemo}
            >
              Book a Demo for ERP <ArrowRight size={15} />
            </button>
          )}
        </div>

        <h2>{title}</h2>
        {subtitle && <p>{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}

function NewLandingPage() {
  const [activeConsulting, setActiveConsulting] = useState("Marketing");
  const [activeERP, setActiveERP] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const [rolePrompt, setRolePrompt] = useState(null);
  const [categories] = useState(defaultItemCategories);
  const [units, setUnits] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [industries, setIndustries] = useState([]);

  // States for country code and mobile number
  const [countryCode, setCountryCode] = useState("+91");
  const [mobileNumber, setMobileNumber] = useState("");
  const [vendorCount, setVendorCount] = useState(0);

  useEffect(() => {
    fetch("/api/potential-vendors/count")
      .then((res) => res.json())
      .then((data) => setVendorCount(data.count))
      .catch((err) => console.error("Failed to load vendor count:", err));
  }, []);

  useEffect(() => {
    fetch("/api/units")
      .then((res) => res.json())
      .then((data) => setUnits(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("Units fetch failed:", err);
        setUnits([]);
      });

    fetch("/api/currencies")
      .then((res) => res.json())
      .then((data) => setCurrencies(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("Currencies fetch failed:", err);
        setCurrencies([]);
      });

    fetch("/api/industries")
      .then((res) => res.json())
      .then((data) => setIndustries(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("Industries fetch failed:", err);
        setIndustries([]);
      });
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [mobileOpen]);

  const scrollTo = (id) => {
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileOpen(false);
  };

  const closeModal = () => setModal(null);

  const submitForm = async (e, formType) => {
    e.preventDefault();
    const formElement = e.target;
    const formData = new FormData(formElement);

    // Prepend country code to mobile if the mobile field is part of the form data
    if (formData.has("mobile")) {
      formData.set("mobile", `${countryCode} ${mobileNumber}`);
    }

    if (formType === "buyer" || formType === "seller") {
      // Final safety check: do NOT save if any email already exists
      const emailsToCheck = [
        { field: "companyEmail", label: "Company Email" },
        { field: "representativeEmail", label: "Representative Email" },
      ];

      for (const { field, label } of emailsToCheck) {
        const email = (formData.get(field) || "").toString().trim();
        if (!email) continue;
        try {
          const exists = await checkEmailExists(email);
          if (exists) {
            window.alert(`${label}: ${EMAIL_EXISTS_MESSAGE}`);
            formElement.elements[field]?.focus();
            return; // stop - data is not saved
          }
        } catch (err) {
          // ignore - server-side validation will still protect the data
        }
      }

      setRolePrompt({ formType, formData });
      return;
    }

    if (formType === "trial") {
      // Final safety check: do NOT save if the trial email already exists
      const trialEmail = (formData.get("email") || "").toString().trim();
      if (trialEmail) {
        try {
          const exists = await checkEmailExists(trialEmail);
          if (exists) {
            window.alert(`Email: ${EMAIL_EXISTS_MESSAGE}`);
            formElement.elements["email"]?.focus();
            return; // stop - data is not saved
          }
        } catch (err) {
          // ignore - server-side validation will still protect the data
        }
      }
    }

    sendFormData(formType, formData);
  };

  const handleRoleSelection = (registerForBoth) => {
    if (!rolePrompt) return;
    const { formType, formData } = rolePrompt;
    const baseCategory = formType === "buyer" ? "Buyer" : "Seller";
    const selectedCategory = registerForBoth ? "Both" : baseCategory;

    formData.set("category", selectedCategory);
    setRolePrompt(null);
    sendFormData(formType, formData);
  };

  const sendFormData = async (formType, formData) => {
    let endpoint = "/api/inquiries";
    let isMultipart = false;

    if (formType === "buyer" || formType === "seller") {
      endpoint = "/api/business-connect";
      isMultipart = true;
    } else if (formType === "demo") {
      endpoint = "/api/demo-request";
    } else if (formType === "trial") {
      endpoint = "/api/trial-request";
    }

    try {
      let response;
      if (isMultipart) {
        response = await fetch(endpoint, {
          method: "POST",
          body: formData,
        });
      } else {
        const jsonObject = Object.fromEntries(formData.entries());
        response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(jsonObject),
        });
      }

      const result = await response.json();
      if (response.ok && result.success) {
        window.alert(
          result.message ||
            "Thank you. Your request has been submitted for review by Synergy5M."
        );
        closeModal();
      } else if (response.status === 409) {
        // Backend rejected because the email already exists
        window.alert(result.message || EMAIL_EXISTS_MESSAGE);
      } else {
        window.alert(
          "Submission error: " + (result.message || "Failed to submit.")
        );
      }
    } catch (err) {
      console.error("Submission failed:", err);
      window.alert(
        "Server error. Please ensure the backend server is reachable."
      );
    }
  };

  const [trialStartDate, setTrialStartDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [trialPlan, setTrialPlan] = useState("7 Days Trial");

  const getTrialEndDate = (startDate, plan) => {
    if (!startDate) return "";
    const start = new Date(startDate);
    let days = 7;
    if (plan === "15 Days Trial") days = 15;
    if (plan === "Paid Plan") days = 365;
    start.setDate(start.getDate() + days);
    return start.toISOString().split("T")[0];
  };

  const openERP = (module) => setActiveERP(module);

  return (
    <div className="synergy-site">
      <header className="site-header">
        <div className="header-inner">
          <button
            className="brand"
            onClick={() => scrollTo("home")}
            aria-label="Synergy5M home"
          >
            <img src={Logo} alt="Synergy5M" />
          </button>

          <button
            className="mobile-menu"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
          >
            <Menu size={24} />
          </button>

          <div
            className={`nav-backdrop ${mobileOpen ? "visible" : ""}`}
            onClick={() => setMobileOpen(false)}
          />

          <nav className={`main-nav ${mobileOpen ? "open" : ""}`}>
            <div className="mobile-drawer-header">
              <img src={Logo} alt="Synergy5M" className="drawer-logo" />
              <button
                className="close-drawer-btn"
                onClick={() => setMobileOpen(false)}
                aria-label="Close navigation"
              >
                <X size={20} />
              </button>
            </div>

            <div className="nav-links-wrap">
              <button className="nav-link" onClick={() => scrollTo("home")}>
                HOME
              </button>

              <button className="nav-link" onClick={() => scrollTo("consulting")}>
                MANAGEMENT CONSULTING
              </button>

              <button className="nav-link" onClick={() => scrollTo("erp")}>
                ERP SOFTWARE
              </button>

              <button className="nav-link" onClick={() => scrollTo("connect")}>
                BUYING & SELLING
              </button>
            </div>

            <div className="drawer-footer-action" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <a
                href="https://synergy5m-business-4-profit-platform.azurewebsites.net/"
                target="_blank"
                rel="noopener noreferrer"
                className="registered-user-btn"
              >
                Registered User
              </a>

              <button
                className="expert-btn"
                onClick={() => {
                  setMobileOpen(false);
                  setModal("expert");
                }}
              >
                Talk to an Expert <ArrowRight size={15} />
              </button>
            </div>
          </nav>
        </div>
      </header>

      <main>
        <section id="home" className="hero section-wrap">
          <div className="hero-copy">
            <div className="eyebrow">SYNERGY5M LLP BUSINESS SOLUTIONS</div>

            <div className="hero-headline-group">
              <h1 className="hero-title-main">One Partner.</h1>
              <div className="text-danger h2">Three Powerful Solutions.</div>
              <div className="hero-title-sub">Unlimited Possibilities.</div>
            </div>

            <p className="hero-desc">
              Empowering businesses with <strong>Consulting Excellence</strong>,{" "}
              <strong>Intelligent ERP</strong> and <strong>Strong Industry Connections.</strong>
            </p>

            <div className="hero-points">
              <div className="point-item">
                <span className="point-icon">
                  <BriefcaseBusiness size={18} />
                </span>
                <div className="point-text">
                  <b>Industry Experience</b><br/>
                  <small>Deep understanding across sectors</small>
                </div>
              </div>

              <div className="point-item">
                <span className="point-icon">
                  <ShieldCheck size={18} />
                </span>
                <div className="point-text">
                  <b>Proven Expertise</b><br/>
                  <small>Solutions that deliver measurable results</small>
                </div>
              </div>

              <div className="point-item">
                <span className="point-icon">
                  <Target size={18} />
                </span>
                <div className="point-text">
                  <b>Growth Focused</b><br/>
                  <small>Helping businesses grow sustainably</small>
                </div>
              </div>
            </div>
          </div>

          <div className="hero-visual">
            <img
              src="/hero-business.png"
              alt="Business, technology and connections"
            />
          </div>
        </section>

        <section
          id="consulting"
          className="consulting section-wrap dark-anchor"
        >
          <div className="section-kicker orange">01</div>
          <div className="consulting-heading">
            <div>
              <h2> MANAGEMENT CONSULTING</h2>
              <h3>The 5M Framework for Business Excellence</h3>
              <p>
                We help businesses optimise every critical element of their
                operations through our proven 5M approach — for stronger
                performance, higher productivity and sustainable growth.
              </p>
            </div>
            <img src="/consulting-dart.png" alt="Business target" />
          </div>
          <div className="consulting-tabs">
            {Object.entries(consulting).map(([name, item]) => {
              const TabIcon = item.Icon;
              return (
                <button
                  key={name}
                  className={activeConsulting === name ? "active" : ""}
                  onClick={() => setActiveConsulting(name)}
                >
                  <span>
                    <TabIcon size={20} />
                  </span>
                  <b>{name}</b>
                </button>
              );
            })}
          </div>
          <div className="consulting-content">
            <div>
              <p className="mini-label">{activeConsulting.toUpperCase()}</p>
              <h3>{consulting[activeConsulting].tagline}</h3>
              <p className="consulting-intro">
                {consulting[activeConsulting].intro}
              </p>
              <ul>
                {consulting[activeConsulting].focus.slice(0, 6).map((x) => (
                  <li key={x}>
                    <Check size={16} />
                    {x}
                  </li>
                ))}
              </ul>
            </div>
            <aside>
              <h4>Key Outcomes</h4>
              {consulting[activeConsulting].outcomes.slice(0, 5).map((x) => (
                <p key={x}>
                  <Check size={15} />
                  {x}
                </p>
              ))}
            </aside>
          </div>
        </section>

        <section id="erp" className="erp section-wrap dark-anchor">
          <div className="section-kicker blue">02</div>
          <div className="erp-intro">
            <div>
              <h2> ERP SOFTWARE</h2>
              <h3>Your Business. Your ERP. Your Brand.</h3>
              <p style={{ paddingBottom: "10px" }}>
                A powerful, integrated ERP software designed for MSMEs to
                automate operations, improve control and drive growth.
              </p>
              <button className="expert-btn" onClick={() => setModal("demo")}>
                Book a Demo <ArrowRight size={15} />
              </button>
            </div>
            <img src="/erp-dashboard.png" alt="SYN ERP 10 dashboard" />
          </div>
          <div className="erp-highlights">
            <div>
              <ShieldCheck />
              <b>White Label ERP</b>
              <small>Your brand, your logo, your identity</small>
            </div>
            <div>
              <Users />
              <b>Unlimited Users</b>
              <small>No per-user restrictions</small>
            </div>
            <div>
              <Boxes />
              <b>Cloud Based</b>
              <small>Secure, reliable access</small>
            </div>
          </div>
          <div className="erp-module-box">
            <div>
              <h3>Powerful Modules. Complete Coverage.</h3>
              <div className="erp-grid">
                {erpModules.map((module) => {
                  const ModuleIcon = module.Icon;
                  return (
                    <button
                      className="erp-module-card"
                      key={module.title}
                      onClick={() => openERP(module)}
                    >
                      <span>
                        <ModuleIcon size={24} />
                      </span>
                      <b>{module.title}</b>
                      <small>
                        {module.covers.slice(0, 4).join(" • ")}
                      </small>
                      <span className="view-details">
                        View details <ArrowRight size={12} />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <aside>
              <h4>SYN ERP 10 Highlights</h4>
              {[
                "Master Driven & Easy to Use",
                "Real-time MIS & Dashboards",
                "Mobile Friendly Access",
                "Advanced Security",
                "Scalable & Flexible",
                "Cost Effective Solution",
              ].map((x) => (
                <p key={x}>
                  <Check size={14} />
                  {x}
                </p>
              ))}
              <button className="expert-btn" onClick={() => setModal("TRail")}>
                Request For Trial
              </button>
            </aside>
          </div>
        </section>

        <section id="connect" className="connect section-wrap dark-anchor">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px", marginBottom: "8px" }}>
            <div className="section-kicker teal" style={{ margin: 0 }}>03</div>


<div
              className="blinking-vendor-badge"
              style={{
                overflow: "hidden",
                background: "linear-gradient(135deg, #a0b6d2 0%, #c7d1df 100%)",
                color: "#ffffff",
                padding: "6px 16px",
                borderRadius: "50px",
                fontSize: "13px",
                fontWeight: "700",
                boxShadow: "0 4px 15px rgba(11, 67, 141, 0.3)",
                display: "flex",
                alignItems: "center",
                letterSpacing: "0.5px",
                border: "1px solid rgba(255,255,255,0.4)"
              }}
            >
              <span style={{
                display: "inline-block",
                whiteSpace: "nowrap",
                color: "black"
              }}>
               Available  <span style={{ color: "#f01a1a", fontSize: "14px" }}>{vendorCount}</span> VENDORS 
              </span>
            </div>




            {/* Attractive Right-Corner Badge Marquee / Animated Pill */}
            <div
              className="blinking-vendor-badge"
              style={{
                overflow: "hidden",
                background: "linear-gradient(135deg, #a0b6d2 0%, #c7d1df 100%)",
                color: "#ffffff",
                padding: "6px 16px",
                borderRadius: "50px",
                fontSize: "13px",
                fontWeight: "700",
                boxShadow: "0 4px 15px rgba(11, 67, 141, 0.3)",
                display: "flex",
                alignItems: "center",
                letterSpacing: "0.5px",
                border: "1px solid rgba(255,255,255,0.4)"
              }}
            >
              <span style={{
                display: "inline-block",
                whiteSpace: "nowrap",
                color: "black"
              }}>
                🔥 WE HAVE <span style={{ color: "#f01a1a", fontSize: "14px" }}>{vendorCount}</span> VENDORS READY TO CONNECT!
              </span>
            </div>
          </div>

          <div className="connect-heading">
            <div>
              <h2>
                Buyer & Sellers
                <br />
                BUYERS TO POTENTIAL SELLERS
              </h2>
              <h3>
                Bridging Requirements. Building Relationships. Creating Value.
              </h3>
              <p>
                We leverage our industry experience and strong connections to
                help buyers find the right suppliers and help sellers connect
                with the right buyers.
              </p>
              <button className="white-btn" onClick={() => setModal("connect")}>
                Connect with Us <ArrowRight size={17} />
              </button>
            </div>
            <img src="/connect-network.png" alt="Business connections" />
          </div>
          <div className="connect-cards">
            <article>
              <ShoppingCart />
              <h3>For Buyers</h3>
              <p>
                Access verified suppliers and competitive offers while keeping
                your requirement confidential during the initial stages.
              </p>
              <ul>
                <li>Access to Pre-verified Suppliers</li>
                <li>Competitive Offers & Best Pricing</li>
                <li>Quality & Timely Delivery Assurance</li>
                <li>Reduced Sourcing Time & Effort</li>
                <li>End-to-End Support</li>
              </ul>
              <button onClick={() => setModal("buyer")}>
                Post Your Requirement
              </button>
            </article>
            <article>
              <Truck />
              <h3>For Sellers</h3>
              <p>
                Connect with genuine buyers and expand your business
                opportunities through qualified requirements.
              </p>
              <ul>
                <li>Connect with Genuine Buyers</li>
                <li>Increase Business Opportunities</li>
                <li>Broaden Market Reach</li>
                <li>Build Long-term Relationships</li>
                <li>Growth With Expert Support</li>
              </ul>
              <button onClick={() => setModal("seller")}>
                List Your Products
              </button>
            </article>
            <article>
              <Handshake />
              <h3>Our Advantage</h3>
              <p>
                Not just a directory — industry experience plus technology to
                qualify and facilitate business.
              </p>
              <ul>
                <li>Deep Industry Experience</li>
                <li>Strong Network & Connections</li>
                <li>Market Intelligence & Insights</li>
                <li>Trusted & Transparent Process</li>
                <li>Win-Win Partnerships</li>
              </ul>
              <button onClick={() => setModal("expert")}>
                Know Our Expertise
              </button>
            </article>
            <aside>
              <b>1000+</b>
              <span>Trusted Suppliers</span>
              <b>5000+</b>
              <span>Buyer Connections</span>
              <b>25+</b>
              <span>Industries Served</span>
              <b>15+</b>
              <span>Years of Experience</span>
            </aside>
          </div>
        </section>

        <section id="about" className="advantage section-wrap">
          <div>
            <span className="section-kicker blue">WHY SYNERGY5M</span>
            <h2>
              Not Just a Marketplace.
              <br />
              <span>An Industry-Experienced Business Connector.</span>
            </h2>
            <p>
              We don't look at your business in isolation. A new order affects
              Marketing. The order requires Materials. Materials require
              Manpower. Production depends on Machines. And the entire cycle
              ultimately impacts Money.
            </p>
          </div>
          <div className="advantage-grid">
            {[
              [
                "Industry Experience",
                "We understand products, specifications, applications and commercial realities.",
              ],
              [
                "Established Industry Connections",
                "Relationships developed through years of professional interaction.",
              ],
              [
                "Confidentiality",
                "Buyer and seller identities remain protected during initial stages.",
              ],
              [
                "Verified Businesses",
                "Controlled registration and business verification.",
              ],
              [
                "Qualified Leads",
                "We focus on genuine commercial requirements rather than simply generating enquiries.",
              ],
              [
                "Human Expertise + Technology",
                "Technology identifies opportunities while industry experience helps qualify them.",
              ],
            ].map(([t, d]) => (
              <article key={t}>
                <Check />
                <h3>{t}</h3>
                <p>{d}</p>
              </article>
            ))}
          </div>
          <div className="statement">
            <strong>We Don't Sell Your Contact Details.</strong>
            <span>We Create Business Opportunities.</span>
            <small>
              Your requirement remains confidential until there is genuine
              commercial intent.
            </small>
          </div>
        </section>

        <section className="process section-wrap">
          <div className="process-head">
            <div>
              <span className="section-kicker teal">BUSINESS CONNECT</span>
              <h2>How Synergy5M Business Connect Works</h2>
            </div>
            <p>
              Verified companies. Qualified connections. Confidential business
              leads.
            </p>
          </div>
          <div className="process-track">
            {connectSteps.map((step, i) => (
              <div key={step}>
                <span>{String(i + 1).padStart(2, "0")}</span>
                <b>{step}</b>
                {i < connectSteps.length - 1 && <ArrowRight size={15} />}
              </div>
            ))}
          </div>
        </section>

        <section
          className="clients-section section-wrap"
          aria-label="Our Clients"
        >
          <div className="clients-head">
            <div>
              <span className="section-kicker orange">OUR CLIENTS</span>
              <h2>Trusted by Businesses.</h2>
            </div>
            <p>
              Our existing client list, now carried forward into the new
              Synergy5M experience.
            </p>
          </div>
          <div className="clients-marquee">
            <div className="clients-track">
              {[...existingClients, ...existingClients].map((client, index) => (
                <div className="client-logo-card" key={`${client.id}-${index}`}>
                  <img src={client.logo} alt={client.name} loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <section className="cta-bar">
        <div>
          <h2>Let's Build Success Together.</h2>
          <p>Technology. Expertise. Connections.</p>
        </div>
        <button onClick={() => setModal("meeting")}>
          Schedule a Meeting <ArrowRight size={17} />
        </button>
      </section>

      <footer id="contact" className="site-footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <img src={Logo} alt="Synergy5M" />
            <p>
              Technology, consulting expertise and industry connections to help
              businesses grow faster and connect smarter.
            </p>
          </div>
          <div>
            <h4>SOLUTIONS</h4>
            <button onClick={() => scrollTo("consulting")}>
              Business Consulting
            </button>
            <button onClick={() => scrollTo("erp")}>SYN ERP 10 Software</button>
            <button onClick={() => scrollTo("connect")}>
              Indenting & Connect
            </button>
          </div>
          <div>
            <h4>BUSINESS CONSULTING</h4>
            {Object.keys(consulting).map((x) => (
              <button
                key={x}
                onClick={() => {
                  setActiveConsulting(x);
                  scrollTo("consulting");
                }}
              >
                {x}
              </button>
            ))}
          </div>
          <div>
            <h4>SYN ERP 10</h4>
            {erpModules.map((x) => (
              <button key={x.title} onClick={() => openERP(x)}>
                {x.title}
              </button>
            ))}
          </div>
          <div>
            <h4>INDENTING & CONNECT</h4>
            <button onClick={() => setModal("buyer")}>For Buyers</button>
            <button onClick={() => setModal("seller")}>For Sellers</button>
            <button onClick={() => setModal("expert")}>Our Expertise</button>
          </div>
          <div>
            <h4>COMPANY</h4>
            <button onClick={() => scrollTo("about")}>About Us</button>
            <button onClick={() => setModal("expert")}>Our Team</button>
            <button onClick={() => setModal("meeting")}>Contact Us</button>
          </div>
        </div>
        <div
          className="footer-bottom"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: "8px",
            color: "#6c757d",
            fontSize: "13px",
          }}
        >
          <span>
            © {new Date().getFullYear()}{" "}
            <span
              onClick={() =>
                window.open(
                  "https://synergy5m.com/admin",
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              style={{
                cursor: "default",
                userSelect: "none",
              }}
            >
              Synergy5M LLP.
            </span>{" "}
            All Rights Reserved.
          </span>
          <span>|</span>
          <span>Privacy Policy &nbsp;|&nbsp; Terms of Use</span>
        </div>
      </footer>

      {activeERP && (
        <Modal
          close={() => setActiveERP(null)}
          title={activeERP.title}
          subtitle={activeERP.tagline}
          wide
          onBookDemo={() => {
            setActiveERP(null);
            setModal("demo");
          }}
        >
          <div className="erp-detail">
            <div className="erp-detail-top">
              <span className="erp-detail-icon">
                {React.createElement(activeERP.Icon, { size: 30 })}
              </span>
              <div>
                <b>What SYN ERP 10 covers</b>
                <p>
                  Integrated process-driven functionality designed around
                  business processes, not software limitations.
                </p>
              </div>
            </div>
            <div className="detail-list">
              {activeERP.covers.map((item) => (
                <div key={item}>
                  <Check size={15} />
                  {item}
                </div>
              ))}
            </div>
            <div className="management-advantage">
              <BadgeCheck size={22} />
              <div>
                <b>Management Advantage</b>
                <span>{activeERP.advantage}</span>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {modal === "buyer" && (
        <Modal
          close={closeModal}
          title="Register & Post a Buying Requirement"
          subtitle="Verified company registration + authorised buyer + complete buying enquiry."
          wide
        >
          <form onSubmit={(e) => submitForm(e, "buyer")}>
            <BuyerForm
              categories={categories}
              units={units}
              currencies={currencies}
              industries={industries}
              countryCode={countryCode}
              setCountryCode={setCountryCode}
              mobileNumber={mobileNumber}
              setMobileNumber={setMobileNumber}
            />
            <div className="form-actions">
              <button className="blue-btn" type="submit">
                Submit for Verification <ArrowRight size={16} />
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "seller" && (
        <Modal
          close={closeModal}
          title="Register & List Your Products"
          subtitle="Verified company registration + authorised seller + complete selling opportunity."
          wide
        >
          <form onSubmit={(e) => submitForm(e, "seller")}>
            <SellerForm
              categories={categories}
              units={units}
              currencies={currencies}
              industries={industries}
              countryCode={countryCode}
              setCountryCode={setCountryCode}
              mobileNumber={mobileNumber}
              setMobileNumber={setMobileNumber}
            />
            <div className="form-actions">
              <button className="blue-btn" type="submit">
                Submit for Verification <ArrowRight size={16} />
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "TRail" && (
        <Modal
          close={closeModal}
          title="Request SYN ERP 10 Trial"
          subtitle="Fill in your company details to start your trial access."
          wide
        >
          <form onSubmit={(e) => submitForm(e, "trial")}>
            <div className="trial-form-grid-four">
              <Field label="Company Name" required>
                <input name="companyName" required placeholder="Company Name" />
              </Field>

              <Field label="Contact Person" required>
                <input
                  name="contactPerson"
                  required
                  placeholder="Contact Person Name"
                />
              </Field>

              <Field label="Mobile No." required>
                <input
                  name="mobileNo"
                  required
                  type="tel"
                  placeholder="Mobile Number"
                />
              </Field>

              {/* Real-time duplicate email check */}
              <EmailField
                label="Email"
                name="email"
                required
                placeholder="Official Email"
              />

              <Field label="GST No.">
                <input name="gstNo" placeholder="GSTIN (Optional)" />
              </Field>

              <Field label="Number of Users">
                <input
                  name="numberOfUsers"
                  type="number"
                  min="1"
                  placeholder="No. of Users"
                />
              </Field>

              <Field label="Subscription Plan" required>
                <select
                  name="subscriptionPlan"
                  required
                  value={trialPlan}
                  onChange={(e) => setTrialPlan(e.target.value)}
                >
                  <option value="7 Days Trial">7 Days Trial</option>
                  <option value="15 Days Trial">15 Days Trial</option>
                  {/* <option value="Paid Plan">Paid Plan</option> */}
                </select>
              </Field>

              <Field label="Trial Status">
                <select name="trialStatus" defaultValue="Active">
                  <option value="Active">Active</option>
                  <option value="Expired">Expired</option>
                  <option value="Converted">Converted</option>
                </select>
              </Field>

              <div className="span-two">
                <Field label="Trial Start Date" required>
                  <input
                    type="date"
                    name="trialStartDate"
                    required
                    value={trialStartDate}
                    onChange={(e) => setTrialStartDate(e.target.value)}
                  />
                </Field>
              </div>

              <div className="span-two">
                <Field label="Trial End Date (Auto Calculate)">
                  <input
                    type="date"
                    name="trialEndDate"
                    readOnly
                    value={getTrialEndDate(trialStartDate, trialPlan)}
                    className="readonly-input"
                  />
                </Field>
              </div>

              <div className="span-two">
                <Field label="Registered Address">
                  <textarea
                    name="address"
                    rows="3"
                    placeholder="Registered business address"
                  />
                </Field>
              </div>

              <div className="span-two">
                <Field label="Remarks">
                  <textarea
                    name="remarks"
                    rows="3"
                    placeholder="Any specific comments or requirements"
                  />
                </Field>
              </div>
            </div>

            <div className="form-actions">
              <button className="blue-btn" type="submit">
                Submit Trial Request <ArrowRight size={16} />
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal &&
        ["expert", "consulting", "connect", "demo", "meeting"].includes(
          modal,
        ) && (
          <Modal
            close={closeModal}
            title={
              modal === "demo"
                ? "Request a SYN ERP 10 Demo"
                : modal === "consulting"
                  ? "Explore Business Consulting"
                  : modal === "connect"
                    ? "Become a Business Connect Partner"
                    : modal === "meeting"
                      ? "Schedule a Meeting"
                      : "Talk to an Expert"
            }
            subtitle={
              modal === "demo"
                ? "Pick your preferred date, time, and meeting platform for a live demonstration."
                : "Share your business details and our team will get in touch with you."
            }
          >
            <form
              key={modal}
              onSubmit={(e) =>
                submitForm(e, modal === "demo" ? "demo" : "inquiry")
              }
            >
              <div className="form-grid">
                <Field label="Your Name" required>
                  <input
                    name="fullName"
                    required
                    placeholder="Enter Your name"
                  />
                </Field>

                <Field label="Business Email" required>
                  <input
                    name="businessEmail"
                    required
                    type="email"
                    placeholder="Business email"
                  />
                </Field>

                <Field label="Company Name" required>
                  <input
                    name="companyName"
                    required
                    placeholder="Company name"
                  />
                </Field>

                <Field label="Official Mobile" required>
                  <input
                    name="officialMobile"
                    required
                    type="tel"
                    placeholder="Business mobile"
                  />
                </Field>

                {modal === "demo" ? (
                  <>
                    <Field label="Preferred Date" required>
                      <input
                        name="preferredDate"
                        required
                        type="date"
                        min={new Date().toISOString().split("T")[0]}
                      />
                    </Field>

                    <Field label="Time Slot" required>
                      <select name="timeSlot" required defaultValue="">
                        <option value="" disabled>
                          Select time slot
                        </option>
                        <option value="10:00 AM - 11:00 AM">
                          10:00 AM - 11:00 AM
                        </option>
                        <option value="11:30 AM - 12:30 PM">
                          11:30 AM - 12:30 PM
                        </option>
                        <option value="02:00 PM - 03:00 PM">
                          02:00 PM - 03:00 PM
                        </option>
                        <option value="03:30 PM - 04:30 PM">
                          03:30 PM - 04:30 PM
                        </option>
                        <option value="05:00 PM - 06:00 PM">
                          05:00 PM - 06:00 PM
                        </option>
                      </select>
                    </Field>

                    <Field label="Meeting Platform" required>
                      <select name="meetingPlatform" required defaultValue="">
                        <option value="" disabled>
                          Select platform
                        </option>
                        <option value="Google Meet">Google Meet</option>
                        <option value="Microsoft Teams">Microsoft Teams</option>
                        <option value="Zoom">Zoom</option>
                      </select>
                    </Field>
                  </>
                ) : (
                  <Field label="Interested In" required>
                    <select
                      name="interestedIn"
                      required
                      defaultValue={
                        modal === "consulting"
                          ? "Business Consulting"
                          : modal === "connect"
                            ? "Business Connect"
                            : "Business Consulting"
                      }
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      <option value="Business Consulting">
                        Business Consulting
                      </option>
                      <option value="SYN ERP 10">SYN ERP 10</option>
                      <option value="Business Connect">Business Connect</option>
                    </select>
                  </Field>
                )}

                <Field
                  label={
                    modal === "demo"
                      ? "Specific Requirements / Focus Areas"
                      : "Requirement"
                  }
                  className="full-span"
                >
                  <textarea
                    name="requirement"
                    rows="3"
                    placeholder={
                      modal === "demo"
                        ? "Tell us about specific modules or processes you would like to see in the demo (optional)"
                        : "Tell us about your requirement"
                    }
                  />
                </Field>
              </div>

              <div className="form-actions">
                <button className="blue-btn" type="submit">
                  {modal === "demo" ? "Schedule Live Demo" : "Submit Enquiry"}{" "}
                  <ArrowRight size={16} />
                </button>
              </div>
            </form>
          </Modal>
        )}

      {rolePrompt && (
        <div className="swal-overlay">
          <div className="swal-modal">
            <div className="swal-icon-wrap">
              <span className="swal-icon-pulse"></span>
              <Handshake size={32} />
            </div>

            <h3 className="swal-title">Additional Registration</h3>

            <p className="swal-text">
              You are applying as a{" "}
              <strong>
                {rolePrompt.formType === "buyer" ? "Buyer" : "Seller"}
              </strong>
              . Would you also like to register your business as a{" "}
              <strong>
                {rolePrompt.formType === "buyer" ? "Seller" : "Buyer"}
              </strong>{" "}
              at the same time?
            </p>

            <div className="swal-actions">
              <button
                type="button"
                className="swal-btn swal-btn-cancel"
                onClick={() => handleRoleSelection(false)}
              >
                No, Just {rolePrompt.formType === "buyer" ? "Buyer" : "Seller"}
              </button>
              <button
                type="button"
                className="swal-btn swal-btn-confirm"
                onClick={() => handleRoleSelection(true)}
              >
                Yes, Register for Both
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NewLandingPage;

const FONT = "'Inter', -apple-system, 'Segoe UI', sans-serif";
const styles = {
    dashboardContainer: { minHeight: "100vh", backgroundColor: "#F2F4F5", fontFamily: FONT },
    topBar: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 28px", background: "#14524A", color: "#fff" },
    topBarLeft: { display: "flex", alignItems: "center", gap: "12px" },
    brandMarkSmall: { width: "34px", height: "34px", borderRadius: "8px", background: "rgba(255,255,255,0.14)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "13px" },
    topBarTitle: { fontSize: "15px", fontWeight: 700 },
    topBarSubtitle: { fontSize: "12px", color: "rgba(255,255,255,0.7)" },
    logoutBtn: { display: "flex", alignItems: "center", gap: "7px", background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.35)", padding: "8px 14px", borderRadius: "7px", cursor: "pointer", fontSize: "13px" },
    pageBody: { padding: "28px", maxWidth: "1280px", margin: "0 auto" },
    label: { display: "block", fontSize: "13px", fontWeight: 600, color: "#3C4550", marginBottom: "7px" },
    input: { width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #D8DCE0", fontSize: "14px", fontFamily: FONT, background: "#FFFFFF" },
    textarea: { width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #D8DCE0", fontSize: "14px", fontFamily: FONT, resize: "vertical", background: "#FFFFFF" },
    primaryBtn: { padding: "12px", background: "#14524A", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "14.5px", fontFamily: FONT },
    emailDispatchGrid: { display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: "20px" },
    emailLeftCard: { background: "#fff", borderRadius: "12px", border: "1px solid #E4E7E9", overflow: "hidden", height: "fit-content" },
    emailRightCard: { background: "#fff", borderRadius: "12px", border: "1px solid #E4E7E9", overflow: "hidden", height: "fit-content" },
    tokenBtn: { background: "#F2F4F5", border: "1px solid #D8DCE0", color: "#3C4550", padding: "4px 9px", borderRadius: "5px", fontSize: "11.5px", cursor: "pointer", fontWeight: 500 }
};